<script>
import Card from '@/components/Card-v2'
import { CreateFlatfindersFromUpload } from './utils'
import Bus from '@/bus'
import Badge from '@/components/Badge'

export default {
  props: {
    project: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      supportedFormats: ['png', 'jpg', 'jpeg', 'webp', 'bmp'],
      promise: null,
      status: 'intro',
      stats: { files: [], flatfinders: [], floorplans: [], masks: [], sunlight: [], skipped: [], processed: [] },
    }
  },
  computed: {
    progress() {
      const a = this.stats.processed.length + this.stats.skipped.length
      const b = this.stats.files.length

      return {
        formatted: [a, b].join(' / '),
        value: b ? a / b : 0,
      }
    },
    summary() {
      const flatfinders = this.stats.flatfinders.length
      const optional = [
        ['flatfinder:import:summaryMasks', this.stats.masks.length],
        ['flatfinder:import:summaryFloorplans', this.stats.floorplans.length],
        ['flatfinder:import:summarySunlight', this.stats.sunlight.length],
      ]

      const items = [
        this.$tc('flatfinder:import:summaryFlatfinders', flatfinders, { count: flatfinders }),
        ...optional.filter(([, count]) => count).map(([key, count]) => this.$tc(key, count, { count })),
      ]

      return this.$t('flatfinder:import:summary', { items: items.join(' + ') })
    },
  },
  methods: {
    run(importer) {
      this.status = 'analyzing'
      this.promise = importer(this.project, {
        onProgress: stats => {
          this.status = 'importing'
          Object.entries(stats).forEach(([k, v]) => (this.stats[k] = v))
        },
      })
        .then(() => {
          Bus.emit('project:refetch')
        })
        .finally(() => {
          this.status = 'done'
        })
    },
    start() {
      this.run(CreateFlatfindersFromUpload)
    },
  },
  components: {
    Card,
    Badge,
  },
}
</script>

<template>
  <Card :title="$tc('importUnitselector', 1)" class="flatfinder-import">
    <div v-if="status === 'intro'" class="flatfinder-import-instructions">
      <h3 v-html="$t('flatfinder:import:title')" />
      <p v-html="$t('flatfinder:import:intro')" />

      <h4 v-html="$t('flatfinder:import:structureTitle')" />
      <p v-html="$t('flatfinder:import:structureBody')" />

      <h4 v-html="$t('flatfinder:import:reservedTitle')" />
      <ul>
        <li v-html="$t('flatfinder:import:reservedRender')" />
        <li v-html="$t('flatfinder:import:reservedMarkings')" />
        <li v-html="$t('flatfinder:import:reservedFloorplans')" />
        <li v-html="$t('flatfinder:import:reservedSunlight')" />
        <li v-html="$t('flatfinder:import:reservedFrames')" />
      </ul>

      <h4 v-html="$t('flatfinder:import:namingTitle')" />
      <p v-html="$t('flatfinder:import:namingBody')" />

      <table class="table table--default table--padding">
        <tbody>
          <tr>
            <td>{{ $t('flatfinder:import:folderOverview') }}/</td>
            <td>← {{ $tc('unitSelector', 1) }}</td>
          </tr>
          <tr>
            <td class="text-indent">render.jpg</td>
            <td>← {{ $tc('backgroundImage', 1) }}</td>
          </tr>
          <tr>
            <td class="text-indent">masks/A-101.png</td>
            <td>← {{ $t('flatfinder:import:markingForUnit', { unit: 'A-101' }) }}</td>
          </tr>
          <tr>
            <td class="text-indent">sunlight/summer/12.00.jpg</td>
            <td>← {{ $t('flatfinder:import:sunlightForSummer') }}</td>
          </tr>
          <tr>
            <td class="text-indent">floorplans/A-101.jpg</td>
            <td>← {{ $t('flatfinder:import:floorplanForUnit', { unit: 'A-101' }) }}</td>
          </tr>
          <tr>
            <td class="text-indent">frames/01/</td>
            <td>← {{ $t('flatfinder:import:framesRepeat') }}</td>
          </tr>
          <tr>
            <td class="text-indent-2">render.jpg</td>
            <td>← {{ $tc('backgroundImage', 1) }}</td>
          </tr>
          <tr>
            <td class="text-indent">frames/02/</td>
            <td>← {{ $t('flatfinder:import:framesRepeat') }}</td>
          </tr>
          <tr>
            <td class="text-indent">{{ $t('flatfinder:import:folderBuilding') }}/</td>
            <td>← {{ $t('flatfinder:import:nested') }}</td>
          </tr>
        </tbody>
      </table>

      <div class="row top g-1 mt-4">
        <h4 class="mr-1">{{ $t('supportedFormats') }}:</h4>
        <Badge v-for="s in supportedFormats" :key="s">.{{ s }}</Badge>
      </div>
    </div>

    <Loader v-if="status === 'analyzing'" value theme="default" message=" " />

    <div v-if="['importing', 'done'].includes(status)">
      <div data-part="list">
        <label>{{ $tc('validFile', 2) }}</label>
        <div>
          <table class="table table--default table--padding">
            <tbody>
              <tr v-for="(e, i) in stats.flatfinders" :key="'ff' + i">
                <td>{{ e }}</td>
                <td>{{ $tc('unitSelector', 1) }}</td>
              </tr>
              <tr v-for="(e, i) in stats.masks" :key="'mask' + i">
                <td>{{ e }}</td>
                <td>{{ $tc('marking', 1) }}</td>
              </tr>
              <tr v-for="(e, i) in stats.floorplans" :key="'fp' + i">
                <td>{{ e }}</td>
                <td>{{ $tc('floorplan', 1) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="stats.skipped.length" data-part="list" class="mt-2">
        <label>{{ $tc('invalidFile', 2) }}</label>
        <div>
          <table class="table table--default table--padding">
            <tbody>
              <tr v-for="(e, i) in stats.skipped" :key="'skip' + i">
                <td>{{ e }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <template #actions>
      <template v-if="status === 'intro'">
        <ButtonComponent :label="$t('cancel')" icon="fa-pro-light:times" @click="$emit('close')" />
        <ButtonComponent :label="$t('startImport')" theme="primary" icon="fa-pro-light:file-import" @click="start" />
      </template>
      <div v-if="status === 'importing'" data-part="progress">
        <div data-part="progress-bar" :style="{ '--p': progress.value }"></div>
        <div data-part="progress-label">{{ progress.formatted }}</div>
      </div>
      <div v-if="status === 'done'" class="row between middle grow-1">
        <div class="row middle g-1">
          <Icon icon="fa-pro-light:check-circle" class="text--success" size="2x" /> <b>{{ $t('finished') }}:</b>
          <span>{{ summary }}</span>
        </div>
        <ButtonComponent :label="$t('close')" @click="$emit('close')" />
      </div>
    </template>
  </Card>
</template>

<style lang="scss">
.flatfinder-import {
  min-width: 650px;
  max-width: 700px;

  [data-part='progress'] {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  [data-part='progress-label'] {
    white-space: nowrap;
  }

  [data-part='list'] {
    label {
      font-weight: bold;
      margin-bottom: 0.5rem;
      display: block;
    }

    & > div {
      max-height: 300px;
      overflow-y: auto;

      border: 1px solid var(--border-color);
      border-radius: var(--border-radius);
    }
  }

  [data-part='progress-bar'] {
    position: relative;
    overflow: clip;
    flex-grow: 1;
    height: 0.5rem;
    min-width: 200px;
    background-color: var(--k-ui-color-neutral);
    border-radius: 5px;

    &:before {
      content: '';
      display: block;
      width: calc(var(--p) * 100%);
      position: absolute;
      top: 0;
      left: 0;
      background-color: var(--primary);
      height: inherit;
    }
  }

  .text-indent {
    text-indent: 1rem;
  }

  .text-indent-2 {
    text-indent: 2rem;
  }

  .flatfinder-import-instructions code {
    font-family: ui-monospace, Menlo, Consolas, monospace;
    font-size: 0.85em;
    padding: 0.1em 0.35em;
    background-color: var(--light-grey);
    border-radius: var(--border-radius);
    white-space: nowrap;
  }
}
</style>
